import {
	DocumentData,
	DocumentReference,
	onSnapshot as firebaseOnSnapshot,
	setDoc,
	updateDoc,
} from "@firebase/firestore";
import { deleteUndefined } from "Helpers/FirestoreHelper";
import { ValidData } from "Store/types/ValidData";
import { Result } from "domain-entities";
import { StoreApi, UseBoundStore, create } from "zustand";

type DocumentRef = DocumentReference<DocumentData, DocumentData>;

interface State<T> {
	isInitializing: boolean;
	error?: Error;
	item?: T;
	ref?: DocumentRef;
	subscribe: (ref: DocumentRef) => () => void;
	setItem: (item: T & ValidData) => Promise<void>;
	updateItem: (item: Partial<T>) => Promise<void>;
	reset: () => void;
}

interface Props<T> {
	is: (object: any) => object is T;
	check: (object: any) => Result;
	onSnapshot?: (item?: T) => void;
}

export function createDocumentStore<T>(
	props: Props<T>
): UseBoundStore<StoreApi<State<T>>> {
	const { is, check, onSnapshot } = props;

	return create<State<T>>((set, get) => ({
		isInitializing: true,
		subscribe: (ref) => {
			set({ ref });

			return firebaseOnSnapshot(
				ref,
				(query) => {
					const item = query.data();

					if (!query.exists) {
						set({ item: undefined });
						void onSnapshot?.(undefined);
						if (query.metadata.fromCache) {
							set({ error: new Error("No Internet connection available.") });
						} else {
							set({ error: new Error("Document does not exist.") });
						}
					} else if (is(item)) {
						set({ item });
						void onSnapshot?.(item);
					} else {
						set({ item: undefined });
						void onSnapshot?.(undefined);
						const result = check(item);

						if (result.reasons?.length) {
							set({
								error: new Error(
									`Check error with reasons: ${result.reasons
										.map((reason) => `${reason.path} ${reason.explanation}`)
										.join(". ")}`
								),
							});
						} else {
							set({ error: new Error("Check error without reasons.") });
						}
					}

					set({ isInitializing: false });
				},
				(error) => {
					set({ isInitializing: false, error, item: undefined });
				}
			);
		},
		setItem: async (item) => {
			const { ref } = get();
			if (!ref) throw new Error("Cannot find reference.");
			await setDoc(ref, deleteUndefined(item));
		},
		updateItem: async (item) => {
			const { ref } = get();
			if (!ref) throw new Error("Cannot find reference.");

			const previousItem = get().item;
			if (previousItem) {
				set({ item: { ...previousItem, ...item } });
			}

			await updateDoc(ref, deleteUndefined(item, true));
		},
		reset: () => {
			set({
				isInitializing: true,
				error: undefined,
				item: undefined,
				ref: undefined,
			});
		},
	}));
}
